/**
 * Local twin of @containd/shared linkOut parse helpers for HTML paint.
 * Keep in sync with packages/shared/src/linkOut.ts and Containd utils/itemLinkOut.ts.
 */

export type ParsedLinkTarget =
  | { kind: 'external'; href: string }
  | { kind: 'item'; ref: string };

const ITEM_PREFIX = /^item:(.+)$/i;

/**
 * First-party hosts: skip leave-site confirm on Spaces freeze viewer.
 * Twin of Containd `TrustedRemoteHosts` TRUSTED_INSTALL_HOSTS (apex; www +
 * subdomains match via suffix). Keep in sync with packages/shared/src/linkOut.ts.
 */
const TRUSTED_EXTERNAL_LINK_HOSTS = new Set([
  'containd.app',
  'containd.space',
  'containd.de',
  'containdapp.com',
  'flyingweasel.de',
  'madweasel.de',
  'madweaselapps.com',
  'madweaselapps.de',
  'nextlevelorder.com',
  'nextlevelorderengineering.com',
  'nloe.eu',
  'nxtlvlordereng.com',
  'shirome.de',
  'dschwenninger.de',
]);

export function parseLinkTarget(raw: string | null | undefined): ParsedLinkTarget | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const itemMatch = trimmed.match(ITEM_PREFIX);
  if (itemMatch) {
    const ref = itemMatch[1]?.trim();
    if (!ref) return null;
    return { kind: 'item', ref };
  }

  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:')
  ) {
    return { kind: 'external', href: trimmed };
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return { kind: 'external', href: trimmed };
  }

  return null;
}

/** Hostname without leading www; empty when not http(s). */
export function extractExternalLinkHost(href: string): string {
  try {
    const url = new URL(href.trim());
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    return url.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

export function isTrustedExternalLinkHost(href: string): boolean {
  const host = extractExternalLinkHost(href);
  if (!host) return false;
  if (TRUSTED_EXTERNAL_LINK_HOSTS.has(host)) return true;
  for (const trusted of TRUSTED_EXTERNAL_LINK_HOSTS) {
    if (host.endsWith('.' + trusted)) return true;
  }
  return false;
}

/**
 * True when Spaces freeze viewer should show a leave-site confirm before navigating.
 * mailto:/tel: and first-party Containd hosts open without confirm.
 */
export function shouldConfirmExternalNavigation(href: string): boolean {
  const trimmed = href.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('mailto:') || lower.startsWith('tel:')) return false;
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    return !isTrustedExternalLinkHost(trimmed);
  }
  // Unknown / custom schemes — confirm rather than silent open.
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
}

/** Primary left-click without modifiers (ctrl/meta/shift/alt). */
export function isPrimaryUnmodifiedClick(e: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): boolean {
  return (
    e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
  );
}

/**
 * When `onExternalLinkOpen` is set and the href needs confirm, intercepts a
 * primary unmodified click. Modifier / middle-click fall through to native href.
 * Always stops propagation so the card does not select.
 * @returns true if the open was delegated to the confirm callback.
 */
export function tryInterceptExternalLinkClick(
  e: {
    button: number;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    preventDefault: () => void;
    stopPropagation: () => void;
  },
  href: string,
  onExternalLinkOpen?: (href: string) => void,
): boolean {
  e.stopPropagation();
  if (
    !onExternalLinkOpen ||
    !shouldConfirmExternalNavigation(href) ||
    !isPrimaryUnmodifiedClick(e)
  ) {
    return false;
  }
  e.preventDefault();
  onExternalLinkOpen(href);
  return true;
}
